/**
 * Player readiness signal for the device connectivity gate.
 *
 * Published to window.__doohPlayable on every schedule evaluation so
 * dooh-player-state (CDP) can decide PLAY vs SETUP without guessing.
 */

export interface DoohPlayable {
  /** True when a valid, non-expired manifest has at least one playable item. */
  hasContent: boolean;
  /** ISO timestamp of manifest.validUntil, or null. */
  manifestValidUntil: string | null;
  /** ISO timestamp when this flag was written. */
  at: string;
}

export interface DoohPlayableWindow {
  __doohPlayable?: DoohPlayable;
  __doohSetupRequested?: string;
  __doohSleep?: DoohSleep | null;
  __doohBacklightControlled?: boolean;
}

/**
 * True when the on-device backlight daemon (dooh-backlight) has announced
 * itself over CDP. While present the player must NOT apply its own CSS
 * brightness filter for sleep mode — daemon + filter would double-dim the
 * panel. The daemon re-asserts the flag on every poll, so a stale page that
 * reloaded mid-sleep re-learns it within seconds.
 */
export function isBacklightControlled(): boolean {
  return (window as unknown as DoohPlayableWindow).__doohBacklightControlled === true;
}

/**
 * Sleep-mode signal for the device backlight daemon (#sleep): polled over CDP
 * by the device gate the same way as __doohPlayable. `brightness` is the
 * target backlight level (0–1) to apply while asleep; the daemon should
 * restore full brightness when this is null/absent.
 */
export interface DoohSleep {
  active: boolean;
  brightness: number;
  at: string;
}

export function publishSleep(signal: DoohSleep | null): void {
  const w = window as unknown as DoohPlayableWindow;
  w.__doohSleep = signal;
}

/** Stale threshold the gate uses; kept here for tests/docs only. */
export const PLAYABLE_STALE_MS = 5 * 60 * 1000;

/** Hold duration (ms) before opening on-screen Wi-Fi setup. */
export const SETUP_HOLD_MS = 10_000;

/** Max pointer movement (px) still counted as a hold. */
export const SETUP_HOLD_MOVE_TOLERANCE_PX = 24;

export function publishPlayable(signal: DoohPlayable): void {
  const w = window as unknown as DoohPlayableWindow;
  w.__doohPlayable = signal;
}

export function requestSetup(at: Date = new Date()): string {
  const iso = at.toISOString();
  const w = window as unknown as DoohPlayableWindow;
  w.__doohSetupRequested = iso;
  return iso;
}

export function buildPlayableSignal(opts: {
  hasContent: boolean;
  manifestValidUntil?: string | null;
  now?: Date;
}): DoohPlayable {
  return {
    hasContent: opts.hasContent,
    manifestValidUntil: opts.manifestValidUntil ?? null,
    at: (opts.now ?? new Date()).toISOString(),
  };
}

/**
 * Compute hasContent from a playback evaluation result.
 * True only when we have at least one item to play (scheduled or emergency).
 * Fallback with empty items / idle / no manifest → false.
 */
export function hasPlayableContent(state: {
  mode: string;
  items: unknown[];
}): boolean {
  if (state.mode === 'emergency') return true;
  return state.mode === 'scheduled' && Array.isArray(state.items) && state.items.length > 0;
}
